import React, { useState, useEffect } from 'react';
import { X, Zap, Calendar, Monitor, AlertCircle, CreditCard } from 'lucide-react';
import { Button } from '../ui/Button';

interface QuickRenewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  client: {
    id: string;
    username: string;
    max_connections: number;
    is_trial?: number | boolean;
    exp_date?: string;
  };
  isLoading?: boolean;
}

const QuickRenewModal: React.FC<QuickRenewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  client,
  isLoading = false
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      setIsVisible(false);
      setTimeout(onClose, 200);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const isTrial = client.is_trial === 1 || client.is_trial === true;
  const connections = client.max_connections || 1;
  const costPerConnection = 1; // 1 crédito por conexão
  const monthsToRenew = 1;
  const totalCost = connections * monthsToRenew * costPerConnection;

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 transition-opacity duration-300 ${
            isVisible ? 'bg-black bg-opacity-50' : 'bg-black bg-opacity-0'
          }`}
        />

        {/* Modal */}
        <div 
          className={`inline-block w-full max-w-md px-6 pt-6 pb-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg ${
            isTrial ? 'border-2 border-purple-300' : 'border-2 border-green-300'
          } ${
            isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`flex-shrink-0 p-2 rounded-full mr-3 ${
                isTrial ? 'bg-purple-50' : 'bg-green-50'
              }`}>
                <Zap className={`w-6 h-6 ${
                  isTrial ? 'text-purple-500' : 'text-green-500'
                }`} />
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                {isTrial ? 'Renovar Teste' : 'Renovação Rápida'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Alert para Testes */}
          {isTrial && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-purple-900">Linha de Teste</p>
                  <p className="text-purple-700 mt-1">
                    Após a renovação, esta linha será convertida para uma conta normal.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="mt-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Cliente: <span className="font-medium text-gray-900">{client.username}</span>
              </p>
            </div>

            {/* Informações da Renovação */}
            <div className="space-y-3">
              {/* Período de Renovação */}
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Período</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">1 mês</span>
              </div>

              {/* Conexões */}
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center">
                  <Monitor className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Conexões</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {connections} tela{connections !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Custo por Conexão */}
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center">
                  <CreditCard className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Custo por conexão</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {costPerConnection} crédito
                </span>
              </div>
            </div>

            {/* Cálculo do Custo */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600 mb-2">
                Cálculo: {connections} conexões × {monthsToRenew} mês × {costPerConnection} crédito
              </div>
              <div className="flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Custo Total</span>
                <span className={`text-2xl font-bold ${
                  isTrial ? 'text-purple-600' : 'text-green-600'
                }`}>
                  {totalCost} {totalCost === 1 ? 'crédito' : 'créditos'}
                </span>
              </div>
            </div>

            {/* Resumo */}
            <div className={`mt-4 p-4 rounded-lg border-2 ${
              isTrial 
                ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200' 
                : 'bg-gradient-to-r from-green-50 to-blue-50 border-green-200'
            }`}>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Resumo</h4>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>✓ Renovação por <strong>1 mês</strong></li>
                <li>✓ <strong>{connections}</strong> conexão{connections !== 1 ? 'ões' : ''} simultânea{connections !== 1 ? 's' : ''}</li>
                <li>✓ Débito de <strong>{totalCost}</strong> crédito{totalCost !== 1 ? 's' : ''}</li>
                {isTrial && (
                  <li className="text-purple-700">⚠ Linha será convertida para conta normal</li>
                )}
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="w-full sm:w-auto mt-3 sm:mt-0"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`w-full sm:w-auto ${
                isTrial 
                  ? 'bg-purple-600 hover:bg-purple-700' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Renovando...
                </div>
              ) : (
                `Renovar por ${totalCost} Crédito${totalCost !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickRenewModal;
